import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import dayjs from 'dayjs'
import specializedReportService, { specializedAttachFormats } from '@api/v1/specializedReportService'
import reportJobService from '@api/v1/reportJobService'
import useLatestRun from '@hooks/useLatestRun'
import SpecializedReportShell from '@components/reports/SpecializedReportShell'
import { timeEntriesSavedCriteria } from '@components/reports/savedRunCriteria'
import SpecializedFilterBar from '@components/reports/SpecializedFilterBar'
import { objectField, dateRangeField, choiceField, boolField } from '@components/reports/specializedFilterFields'
import ReportViewsMenu from '@components/reports/ReportViewsMenu'
import useSavedReportView from '@components/reports/useSavedReportView'
import { describeFilters, dateRangePart } from '@components/reports/reportFilterSummary'
import CsvReportGrid from '@components/reports/CsvReportGrid'

// CB system constants — stable across all tenants.
const TYPE_USERS = 1
const TYPE_COMPANIES = 3
const TYPE_MATTERS = 1001

// Tenant-specific custom field IDs are resolved server-side from CB
// server settings; cb-api injects them into the filter before calling
// the legacy CSV-export endpoint. See REPORT_TENANT_SETTINGS in
// cb-api/src/domains/reports/routes.js.

// `billableObjectType` has no control and must survive every edit — it tells the
// proc which object type the entries hang off. The three selects are
// mutually-exclusive tri-states, so they are RADIO (single-select): letting a
// user pick Billed AND Unbilled is meaningless and the proc cannot honour it
// (CC-49861). `isBilled`/`isBillable` are ints on the wire; `isGrouped` is a
// bool (CC-49922 — V1's jqxSwitchButton, default Yes).
const TimeEntriesFIELDS = [
  objectField({ key: 'client', label: 'Client', typeId: TYPE_COMPANIES, idKey: 'clientId', nameKey: 'clientName' }),
  objectField({ key: 'matter', label: 'Matter', typeId: TYPE_MATTERS, idKey: 'billingObjectId', nameKey: 'billingObjectName' }),
  objectField({ key: 'timekeeper', label: 'Timekeeper', typeId: TYPE_USERS, idKey: 'userId', nameKey: 'userName' }),
  objectField({ key: 'respAtty', label: 'Responsible Attorney', typeId: TYPE_USERS, idKey: 'respAttyId', nameKey: 'respAttyName' }),
  dateRangeField({ key: 'entryDates', label: 'Date Range', startKey: 'entryDateStart', endKey: 'entryDateEnd', omitAll: true }),
  choiceField({
    key: 'isBilled', label: 'Is Billed?', valueKey: 'isBilled',
    options: [{ value: 0, label: 'Both' }, { value: 1, label: 'Billed' }, { value: 2, label: 'Unbilled' }],
    decode: (s) => parseInt(s, 10) || 0,
  }),
  choiceField({
    key: 'isBillable', label: 'Is Billable?', valueKey: 'isBillable',
    options: [{ value: 0, label: 'Both' }, { value: 1, label: 'Billable' }, { value: 2, label: 'Non-billable' }],
    decode: (s) => parseInt(s, 10) || 0,
  }),
  boolField({ key: 'isGrouped', label: 'Group Entries?', valueKey: 'isGrouped' }),
]

const makeDefaults = () => ({
  clientId: null, clientName: '',
  userId: null, userName: '',
  respAttyId: null, respAttyName: '',
  billingObjectId: null, billingObjectName: '',
  entryDateStart: dayjs().startOf('month').format('YYYY-MM-DD'),
  entryDateEnd: dayjs().endOf('month').format('YYYY-MM-DD'),
  isBilled: 0,    // 0 both, 1 billed, 2 unbilled
  isBillable: 0,  // 0 both, 1 billable, 2 non-billable
  isGrouped: true,
  billableObjectType: TYPE_MATTERS,
})

// Module-level so useLatestRun's `run` keeps one identity (spec 131).
const runErrorMessage = (err) => err?.response?.data?.error || err.message || 'Report failed'

const TimeEntriesReport = ({ reportId, name = 'Time Entries Report', summary, jobId }) => {
  const [filters, setFilters] = useState(makeDefaults)
  // Spec 131: only the NEWEST run may write the rows, the spinner, the error or
  // the documents' criteria (the split button's mid-run "Run report now" is also
  // disabled now; this gate is the structural guarantee).
  const {
    running, error, ran, runKey, run: startRun, invalidate: invalidateRun,
  } = useLatestRun({ initialRunning: !!jobId, errorMessage: runErrorMessage })
  // CC-50013 (G3): saved views. The hook carries the grid's page state, so
  // the menu cannot be mounted without it (the CC-49732 defect).
  const view = useSavedReportView(setFilters)
  // CC-50029: exports state the period they cover.
  const filtersSummary = useMemo(() => describeFilters(dateRangePart(filters.entryDateStart, filters.entryDateEnd)), [filters])
  const [data, setData] = useState({ headers: [], rows: [], meta: [] })
  const [savedAsOf, setSavedAsOf] = useState(null)
  // Spec 185 (CC-53435): the reopened job's criteria line, built from ITS stored params
  // at the reopen's commit (null = the shell's "not recorded"). Never seeded into
  // `filters`: the filter bar is not the saved run's, and a reopen must not start a run.
  const [savedCriteria, setSavedCriteria] = useState(null)
  // Consume-once: the first run reopens the tray's saved job; later runs are fresh.
  const jobRef = useRef(jobId || null)

  const runReport = useCallback(() => {
    if (!filters.entryDateStart || !filters.entryDateEnd) {
      // Spec 131: the refusal also retires a run still in flight.
      invalidateRun({ error: 'Date range is required.' })
      return undefined
    }
    return startRun(async () => {
      const jid = jobRef.current
      jobRef.current = null // consumed — subsequent runs are fresh
      if (jid) {
        // Tray "View report" → reopen the exact saved snapshot + show its run time.
        const [st, r] = await Promise.all([reportJobService.status(jid), reportJobService.result(jid)])
        return () => { setSavedAsOf(st?.createdAt || null); setSavedCriteria(timeEntriesSavedCriteria(st?.savedParams)); setData(r?.data || { headers: [], rows: [], meta: [] }) }
      }
      const result = await specializedReportService.timeEntries.run(filters, { reportId })
      // Spec 131 decision 3: the saved-result banner clears at the COMMIT.
      return () => { setSavedAsOf(null); setData(result || { headers: [], rows: [], meta: [] }) }
    }, { criteria: { filtersSummary } })
  }, [filters, reportId, startRun, invalidateRun, filtersSummary])

  // Spec 131: deferred and cancellable, so StrictMode's simulated remount starts
  // ONE mount run, not two.
  useEffect(() => {
    const t = setTimeout(() => { runReport() }, 0)
    return () => clearTimeout(t)
  }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  // CC-50013 G2 / CC-50148: shared metadata-driven bar (see TimeEntriesFIELDS).
  const filterBar = (
    <SpecializedFilterBar
      fields={TimeEntriesFIELDS}
      filters={filters}
      onFiltersChange={setFilters}
      defaults={makeDefaults()}
      onRun={runReport}
      isLoading={running}
      queue={{
        reportId,
        name,
        actionKey: 'time-entries',
        endpoint: (f) => ({ method: 'POST', path: '/reports/time-entries', body: f }),
        // Spec 200: an emailed copy carries this report's own export, asked what the run asks.
        attachFormats: specializedAttachFormats('/reports/time-entries'),
      }}
      actions={(
        <ReportViewsMenu
          storageKey="report:time-entries"
          currentFilters={filters}
          onApply={view.applyView}
          getPageState={view.getPageState}
        />
      )}
    />
  )


  return (
    <SpecializedReportShell
      name={name} summary={summary} running={running} error={error}
      ranCriteria={ran?.criteria ?? null} runKey={runKey}
      filterBar={filterBar} filtersSummary={filtersSummary} summaryData={data} savedAsOf={savedAsOf}
      savedRunFiltersSummary={savedAsOf != null ? savedCriteria : undefined}
      download={(format, getToken) =>
        specializedReportService.timeEntries.download(filters, format, getToken, `time-entries.${format}`)
      }
    >
      <CsvReportGrid key={view.gridKey} data={data} storageKey="report:time-entries" defaultSortLabel="Client" pageStateRef={view.pageStateRef} initialPageState={view.initialPageState} />
    </SpecializedReportShell>
  )
}

export default TimeEntriesReport
